import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { memberAdminCodes, useAuth } from '../../lib/auth';
import type { Member, MemberCert, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

/**
 * There is no GET endpoint that lists a member's certificates (grep confirms
 * packages/modules/c12-seyx/src/routes/registry.ts only ever returns a cert
 * as the 201 body of POST .../certs). Same workaround govfin-portal's
 * Debt.tsx uses for a missing GET /instruments listing: remember what this
 * browser tab registered, in sessionStorage, so revoke has a fingerprint to
 * act on. A page reload forgets certs registered in an earlier session —
 * that's an honest reflection of what the API can tell this console, not a
 * bug in this component.
 */
interface SessionCert extends MemberCert {
  mdaCode: string;
}
const CERTS_KEY = 'seyx.certsSession';

function readCerts(): SessionCert[] {
  const raw = sessionStorage.getItem(CERTS_KEY);
  return raw ? (JSON.parse(raw) as SessionCert[]) : [];
}

const SAMPLE_PEM = `-----BEGIN CERTIFICATE-----\n${'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA'.repeat(4)}\n-----END CERTIFICATE-----`;

export function Members() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isDict = roles.includes('SEYX_ADMIN');
  const myMdas = memberAdminCodes(roles);
  const canSeeAnything = isDict || myMdas.length > 0;

  const [status, setStatus] = useState('');
  const [showOnboard, setShowOnboard] = useState(false);
  const [certTarget, setCertTarget] = useState<string | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<Member | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<SessionCert | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sessionCerts, setSessionCerts] = useState<SessionCert[]>(() => readCerts());

  const persistCerts = (items: SessionCert[]) => {
    setSessionCerts(items);
    sessionStorage.setItem(CERTS_KEY, JSON.stringify(items));
  };

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (status) p.set('status', status);
    return `/api/v1/seyx/members?${p}`;
  }, [status]);
  const listQ = useApiQuery<Page<Member>>(isDict ? query : null);

  const counts = useMemo(() => {
    const items = listQ.data?.items ?? [];
    return {
      total: items.length,
      active: items.filter((m) => m.status === 'ACTIVE').length,
      pending: items.filter((m) => m.status === 'PENDING').length,
      suspended: items.filter((m) => m.status === 'SUSPENDED').length,
    };
  }, [listQ.data]);

  const setMemberStatus = async (m: Member, action: 'activate' | 'suspend', reason?: string) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/seyx/members/${m.mdaCode}/${action}`, action === 'suspend' ? { reason } : undefined);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    }
  };

  const visibleCerts = isDict ? sessionCerts : sessionCerts.filter((c) => myMdas.includes(c.mdaCode));

  if (!canSeeAnything) {
    return (
      <div>
        <PageHeader title="Members" subtitle="MDA onboarding &amp; certificate lifecycle for the data exchange layer" />
        <EmptyState title="No access" hint="This screen needs SEYX_ADMIN, or a MEMBER_ADMIN:<code> role for your own MDA." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Members"
        subtitle="MDA onboarding & certificate lifecycle for the data exchange layer"
        actions={
          isDict ? (
            <button className="btn-primary" onClick={() => setShowOnboard(true)}>
              + Onboard member
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {isDict ? (
        <>
          <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Members loaded" value={counts.total} hint={listQ.data?.nextCursor ? 'more than 100 — refine the filter' : 'up to 100'} />
            <StatCard label="Active" value={counts.active} tone="good" />
            <StatCard label="Pending activation" value={counts.pending} tone={counts.pending > 0 ? 'warn' : 'default'} />
            <StatCard label="Suspended" value={counts.suspended} tone={counts.suspended > 0 ? 'bad' : 'default'} />
          </div>

          <div className="mb-4">
            <select className="field-input max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
          </div>

          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No members onboarded yet" hint="Onboard the first MDA to the exchange layer." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">MDA code</th>
                    <th className="table-head">Name</th>
                    <th className="table-head">Security server</th>
                    <th className="table-head">Status</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50">
                      <td className="table-cell font-mono text-xs">{m.mdaCode}</td>
                      <td className="table-cell">{m.name}</td>
                      <td className="table-cell text-xs text-slate-500">{m.securityServerUrl}</td>
                      <td className="table-cell">
                        <StatusBadge status={m.status} />
                        {m.statusReason ? <div className="mt-0.5 text-[11px] text-slate-400">{m.statusReason}</div> : null}
                      </td>
                      <td className="table-cell text-right">
                        <div className="flex justify-end gap-1.5">
                          <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setCertTarget(m.mdaCode)}>
                            + Cert
                          </button>
                          {m.status !== 'ACTIVE' ? (
                            <button className="btn-success !px-2 !py-0.5 text-xs" onClick={() => setMemberStatus(m, 'activate')}>
                              Activate
                            </button>
                          ) : null}
                          {m.status !== 'SUSPENDED' ? (
                            <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => setSuspendTarget(m)}>
                              Suspend
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="mb-6 card p-5">
          <h2 className="text-sm font-bold text-slate-700">Your MDA{myMdas.length > 1 ? 's' : ''}: {myMdas.join(', ')}</h2>
          <p className="mt-1 text-xs text-slate-500">
            The member directory listing is DICT-only (SEYX_ADMIN) — but you can still register a certificate for your own
            MDA below.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {myMdas.map((code) => (
              <button key={code} className="btn-primary" onClick={() => setCertTarget(code)}>
                + Register certificate for {code}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="mb-1 text-sm font-bold text-slate-700">Certificates registered this session</h2>
        <p className="mb-2 text-xs text-slate-400">
          There is no GET .../certs listing endpoint yet — this app remembers what you register in this browser tab so
          you can revoke it later. Reloading the page, or a cert registered in an earlier session, won't appear here.
        </p>
        <div className="card overflow-x-auto">
          {visibleCerts.length === 0 ? (
            <EmptyState title="No certificates registered this session" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">MDA</th>
                  <th className="table-head">Fingerprint</th>
                  <th className="table-head">Subject DN</th>
                  <th className="table-head">Expires</th>
                  <th className="table-head">Registered</th>
                  {isDict ? <th className="table-head" /> : null}
                </tr>
              </thead>
              <tbody>
                {visibleCerts.map((c) => (
                  <tr key={c.id}>
                    <td className="table-cell font-mono text-xs">{c.mdaCode}</td>
                    <td className="table-cell font-mono text-xs">{c.fingerprint.slice(0, 16)}…</td>
                    <td className="table-cell text-xs">{c.subjectDn}</td>
                    <td className="table-cell text-slate-500">{formatDate(c.notAfter)}</td>
                    <td className="table-cell text-slate-500">{formatDateTime(c.createdAt)}</td>
                    {isDict ? (
                      <td className="table-cell text-right">
                        <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => setRevokeTarget(c)}>
                          Revoke
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showOnboard ? (
        <OnboardModal
          onClose={() => setShowOnboard(false)}
          onDone={() => {
            setShowOnboard(false);
            listQ.reload();
          }}
        />
      ) : null}
      {certTarget ? (
        <CertModal
          mdaCode={certTarget}
          onClose={() => setCertTarget(null)}
          onDone={(cert) => {
            setCertTarget(null);
            persistCerts([{ ...cert, mdaCode: certTarget }, ...sessionCerts]);
          }}
        />
      ) : null}
      {suspendTarget ? (
        <SuspendModal
          member={suspendTarget}
          onClose={() => setSuspendTarget(null)}
          onSubmit={async (reason) => {
            await setMemberStatus(suspendTarget, 'suspend', reason);
            setSuspendTarget(null);
          }}
        />
      ) : null}
      {revokeTarget ? (
        <RevokeModal
          cert={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onDone={() => {
            persistCerts(sessionCerts.filter((c) => c.id !== revokeTarget.id));
            setRevokeTarget(null);
          }}
          onError={setActionError}
        />
      ) : null}
    </div>
  );
}

function OnboardModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [mdaCode, setMdaCode] = useState('');
  const [name, setName] = useState('');
  const [securityServerUrl, setSecurityServerUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/seyx/members', { mdaCode, name, securityServerUrl });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Onboarding failed');
    } finally {
      setBusy(false);
    }
  };

  const validCode = /^[A-Z0-9_]{2,20}$/.test(mdaCode);

  return (
    <Modal title="Onboard member MDA" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="MDA code (A-Z, 0-9, _)">
            <input
              className="field-input font-mono"
              value={mdaCode}
              onChange={(e) => setMdaCode(e.target.value.toUpperCase())}
              placeholder="SRC"
              maxLength={20}
            />
          </Field>
          <Field label="Security server URL">
            <input
              className="field-input"
              value={securityServerUrl}
              onChange={(e) => setSecurityServerUrl(e.target.value)}
              placeholder="https://ss.src.gov.sc"
            />
          </Field>
        </div>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Seychelles Revenue Commission" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !validCode || name.length < 3 || !securityServerUrl}>
            {busy ? 'Onboarding…' : 'Onboard'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CertModal({
  mdaCode,
  onClose,
  onDone,
}: {
  mdaCode: string;
  onClose: () => void;
  onDone: (cert: MemberCert) => void;
}) {
  const [certificatePem, setCertificatePem] = useState('');
  const [subjectDn, setSubjectDn] = useState(`CN=ss.${mdaCode.toLowerCase()}.gov.sc,O=${mdaCode},C=SC`);
  const [notAfter, setNotAfter] = useState(() => new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const cert = await api.post<MemberCert>(`/api/v1/seyx/members/${mdaCode}/certs`, {
        certificatePem,
        subjectDn,
        notAfter: new Date(`${notAfter}T00:00:00.000Z`).toISOString(),
      });
      onDone(cert);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Certificate registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Register certificate — ${mdaCode}`} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Subject DN">
          <input className="field-input" value={subjectDn} onChange={(e) => setSubjectDn(e.target.value)} />
        </Field>
        <Field label="Expires">
          <input className="field-input max-w-[200px]" type="date" value={notAfter} onChange={(e) => setNotAfter(e.target.value)} />
        </Field>
        <Field label="Certificate (PEM)">
          <textarea
            className="field-input font-mono text-xs"
            rows={6}
            value={certificatePem}
            onChange={(e) => setCertificatePem(e.target.value)}
            placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
          />
        </Field>
        <button className="btn-secondary !py-1.5 text-xs" type="button" onClick={() => setCertificatePem(`${SAMPLE_PEM}\n${mdaCode}-${Date.now()}`)}>
          Fill sample certificate (dev only)
        </button>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || certificatePem.length < 50 || subjectDn.length < 3}>
            {busy ? 'Registering…' : 'Register certificate'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SuspendModal({ member, onClose, onSubmit }: { member: Member; onClose: () => void; onSubmit: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit(reason);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Suspend member — ${member.mdaCode}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">A suspended member drops out of the exchange routing table immediately.</p>
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Suspending…' : 'Suspend'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RevokeModal({
  cert,
  onClose,
  onDone,
  onError,
}: {
  cert: SessionCert;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/v1/seyx/members/${cert.mdaCode}/certs/${cert.fingerprint}/revoke`, { reason });
      onDone();
    } catch (e) {
      onError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Revocation failed');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Revoke certificate — ${cert.mdaCode}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500 font-mono">{cert.fingerprint}</p>
        <Field label="Reason">
          <textarea className="field-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Revoking…' : 'Revoke'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
